import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, Palette, Type, Mail, MessageSquare, Shield, UserPlus, Trash2, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', email: '', password: '' });
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [settings, setSettings] = useState({
    navbar_transparent_color: '#ffffff',
    navbar_scroll_color: '#1e3a8a',
    hero_title: 'Te ayudamos a crear auténticos Viajes de Ensueño',
    hero_subtitle: 'En Viajes de Ensueño creamos experiencias personalizadas para nuestros clientes, ofreciendo los mejores servicios. No solo planificamos viajes, sino que os ayudamos a crear recuerdos que no olvidaréis jamás.',
    hero_cta_primary: 'Planifica tu Viaje',
    hero_cta_secondary: 'Conócenos',
    pillars_title: '¿Por qué elegirnos?',
    pillar_1_title: 'Expertos',
    pillar_1_description: 'Somos expertos en los destinos que trabajamos. Gracias a ello, creamos el viaje que has soñado.',
    pillar_2_title: 'Personalizados',
    pillar_2_description: 'Cada viaje es único. Diseñamos experiencias a medida según tus gustos, intereses y presupuesto.',
    pillar_3_title: 'Confianza',
    pillar_3_description: 'Tu tranquilidad es nuestra prioridad. Trabajamos con los mejores proveedores del sector.',
    pillar_4_title: 'Atención 24/7',
    pillar_4_description: 'Estamos contigo antes, durante y después de tu viaje. Asistencia completa en todo momento.',
    pillar_5_title: 'Experiencias Únicas',
    pillar_5_description: 'Acceso a experiencias exclusivas que transformarán tu viaje en algo extraordinario.',
    pillar_6_title: 'Sin Fronteras',
    pillar_6_description: 'Desde escapadas cercanas hasta destinos exóticos. El mundo entero está a tu alcance.',
    custom_trips_title: 'Viajes a Medida',
    custom_trips_subtitle: 'Ya no se viaja para conocer lugares sino para vivirlos. Déjanos conocerte y organizaremos tu viaje de ensueño.',
    custom_trips_button: 'Diseña tu Viaje',
    newsletter_title: 'Recibe nuestras mejores ofertas',
    newsletter_subtitle: 'Suscríbete a nuestra newsletter y sé el primero en conocer las ofertas más exclusivas y descuentos especiales.',
    newsletter_placeholder: 'Tu email',
    newsletter_button: 'Suscribirse',
    footer_description: 'Creamos experiencias personalizadas para nuestros clientes, ofreciendo los mejores servicios de viaje.',
    footer_address: 'C./ Cosme Damian Churruca 6, 28660 Boadilla del Monte - Madrid',
    footer_phone: '91 125 63 52 / 678 744 482',
    footer_email: 'reservas@viajesensueno.com',
    whatsapp_phone: '34678744482',
    contact_email: 'reservas@viajesensueno.com'
  });

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;

  useEffect(() => {
    loadSettings();
    loadAdmins();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      console.log('Settings loaded from API:', res.data);
      setSettings(prev => ({
        ...prev,
        ...res.data
      }));
    } catch (err) {
      toast.error('Error al cargar configuración');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const res = await axios.get(`${API}/auth/admins`, config);
      setAdmins(res.data);
    } catch (err) {
      console.error('Error loading admins:', err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      if (!newAdmin.username || !newAdmin.email || !newAdmin.password) {
        toast.error('Todos los campos son obligatorios');
        return;
      }
      await axios.post(`${API}/auth/admins`, newAdmin, config);
      toast.success('Administrador añadido correctamente');
      setNewAdmin({ username: '', email: '', password: '' });
      setShowAddAdmin(false);
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al añadir administrador');
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este administrador? Perderá el acceso al panel inmediatamente.')) return;
    try {
      await axios.delete(`${API}/auth/admins/${id}`, config);
      toast.success('Administrador eliminado');
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleResetMfa = async (admin) => {
    if (!window.confirm(`¿Resetear el Authenticator de ${admin.username}? Tendrá que configurarlo de nuevo con un QR en su próximo inicio de sesión.`)) return;
    try {
      await axios.post(`${API}/auth/admins/${admin.id}/reset-mfa`, {}, config);
      toast.success('Authenticator reseteado');
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resetear el Authenticator');
    }
  };

  const handleSaveSection = async (sectionName, fieldsToSave) => {
    setSaving(prev => ({ ...prev, [sectionName]: true }));
    try {
      const params = new URLSearchParams();
      
      // Solo enviar los campos de esta sección
      fieldsToSave.forEach(field => {
        params.append(field, settings[field] || '');
      });
      
      // Enviar todos los campos actuales para mantener el resto
      Object.keys(settings).forEach(key => {
        if (!fieldsToSave.includes(key)) {
          params.append(key, settings[key] || '');
        }
      });
      
      console.log('Saving section:', sectionName, 'with params:', params.toString());
      
      await axios.put(`${API}/settings?${params.toString()}`, {}, config);
      toast.success(`${sectionName} guardado correctamente`);
      
      // Recargar para confirmar
      await loadSettings();
    } catch (err) {
      toast.error(`Error al guardar ${sectionName}`);
      console.error('Save error:', err);
    } finally {
      setSaving(prev => ({ ...prev, [sectionName]: false }));
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-3xl font-bold">Configuración</h1>
            <p className="text-muted-foreground">Gestiona la web y accesos de administración</p>
          </div>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
            <TabsTrigger value="general">Diseño General</TabsTrigger>
            <TabsTrigger value="admins" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Administradores
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Cargando configuración...</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {/* Navbar Colors Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Colores del Menú Superior</h2>
                </div>
                <Button 
                  onClick={() => handleSaveSection('Navbar', ['navbar_transparent_color', 'navbar_scroll_color'])}
                  disabled={saving.Navbar}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving.Navbar ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              <div className="space-y-6">
                {/* Transparent Color */}
                <div className="space-y-3">
                  <Label htmlFor="transparent-color">
                    Color del texto (menú transparente)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Se aplica cuando el navbar está sobre el video/imagen hero
                  </p>
                  <div className="flex gap-4 items-center">
                    <div className="relative">
                      <Input
                        id="transparent-color"
                        type="color"
                        value={settings.navbar_transparent_color}
                        onChange={(e) => handleChange('navbar_transparent_color', e.target.value)}
                        className="w-24 h-12 cursor-pointer"
                      />
                    </div>
                    <Input
                      type="text"
                      value={settings.navbar_transparent_color}
                      onChange={(e) => handleChange('navbar_transparent_color', e.target.value)}
                      className="w-32 font-mono"
                      placeholder="#ffffff"
                    />
                    <div 
                      className="flex-1 h-12 rounded border flex items-center justify-center text-sm font-medium"
                      style={{ 
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        color: settings.navbar_transparent_color 
                      }}
                    >
                      Vista previa
                    </div>
                  </div>
                </div>

                {/* Scroll Color */}
                <div className="space-y-3">
                  <Label htmlFor="scroll-color">
                    Color del texto (después de scroll)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Se aplica cuando haces scroll y el fondo es blanco
                  </p>
                  <div className="flex gap-4 items-center">
                    <div className="relative">
                      <Input
                        id="scroll-color"
                        type="color"
                        value={settings.navbar_scroll_color}
                        onChange={(e) => handleChange('navbar_scroll_color', e.target.value)}
                        className="w-24 h-12 cursor-pointer"
                      />
                    </div>
                    <Input
                      type="text"
                      value={settings.navbar_scroll_color}
                      onChange={(e) => handleChange('navbar_scroll_color', e.target.value)}
                      className="w-32 font-mono"
                      placeholder="#1e3a8a"
                    />
                    <div 
                      className="flex-1 h-12 rounded border flex items-center justify-center text-sm font-medium"
                      style={{ 
                        backgroundColor: '#ffffff',
                        color: settings.navbar_scroll_color 
                      }}
                    >
                      Vista previa
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Hero Section Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Sección Hero (Portada)</h2>
                </div>
                <Button 
                  onClick={() => handleSaveSection('Hero', ['hero_title', 'hero_subtitle', 'hero_cta_primary', 'hero_cta_secondary'])}
                  disabled={saving.Hero}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving.Hero ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="hero-title">Título Principal</Label>
                  <Input
                    id="hero-title"
                    value={settings.hero_title}
                    onChange={(e) => handleChange('hero_title', e.target.value)}
                    placeholder="Te ayudamos a crear auténticos Viajes de Ensueño"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="hero-subtitle">Subtítulo</Label>
                  <Textarea
                    id="hero-subtitle"
                    value={settings.hero_subtitle}
                    onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                    placeholder="Descripción del servicio..."
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hero-cta-primary">Botón Principal</Label>
                    <Input
                      id="hero-cta-primary"
                      value={settings.hero_cta_primary}
                      onChange={(e) => handleChange('hero_cta_primary', e.target.value)}
                      placeholder="Planifica tu Viaje"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hero-cta-secondary">Botón Secundario</Label>
                    <Input
                      id="hero-cta-secondary"
                      value={settings.hero_cta_secondary}
                      onChange={(e) => handleChange('hero_cta_secondary', e.target.value)}
                      placeholder="Conócenos"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Newsletter Section Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Sección Newsletter</h2>
                </div>
                <Button 
                  onClick={() => handleSaveSection('Newsletter', ['newsletter_title', 'newsletter_subtitle', 'newsletter_placeholder', 'newsletter_button'])}
                  disabled={saving.Newsletter}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving.Newsletter ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="newsletter-title">Título</Label>
                  <Input
                    id="newsletter-title"
                    value={settings.newsletter_title}
                    onChange={(e) => handleChange('newsletter_title', e.target.value)}
                    placeholder="Recibe nuestras mejores ofertas"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="newsletter-subtitle">Subtítulo</Label>
                  <Textarea
                    id="newsletter-subtitle"
                    value={settings.newsletter_subtitle}
                    onChange={(e) => handleChange('newsletter_subtitle', e.target.value)}
                    placeholder="Descripción de la newsletter..."
                    rows={2}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="newsletter-placeholder">Placeholder del Input</Label>
                    <Input
                      id="newsletter-placeholder"
                      value={settings.newsletter_placeholder}
                      onChange={(e) => handleChange('newsletter_placeholder', e.target.value)}
                      placeholder="Tu email"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newsletter-button">Texto del Botón</Label>
                    <Input
                      id="newsletter-button"
                      value={settings.newsletter_button}
                      onChange={(e) => handleChange('newsletter_button', e.target.value)}
                      placeholder="Suscribirse"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Pillars Section Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Sección "Por Qué Elegirnos" (6 Pilares)</h2>
                </div>
                <Button 
                  onClick={() => handleSaveSection('Pilares', [
                    'pillars_title',
                    'pillar_1_title', 'pillar_1_description',
                    'pillar_2_title', 'pillar_2_description',
                    'pillar_3_title', 'pillar_3_description',
                    'pillar_4_title', 'pillar_4_description',
                    'pillar_5_title', 'pillar_5_description',
                    'pillar_6_title', 'pillar_6_description'
                  ])}
                  disabled={saving.Pilares}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving.Pilares ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="pillars-title">Título de la Sección</Label>
                  <Input
                    id="pillars-title"
                    value={settings.pillars_title}
                    onChange={(e) => handleChange('pillars_title', e.target.value)}
                    placeholder="¿Por qué elegirnos?"
                    className="mt-2"
                  />
                </div>

                {[1, 2, 3, 4, 5, 6].map(num => (
                  <div key={num} className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <h3 className="font-medium text-sm text-gray-700">Pilar {num}</h3>
                    <div>
                      <Label htmlFor={`pillar-${num}-title`}>Título</Label>
                      <Input
                        id={`pillar-${num}-title`}
                        value={settings[`pillar_${num}_title`]}
                        onChange={(e) => handleChange(`pillar_${num}_title`, e.target.value)}
                        placeholder={`Título del pilar ${num}`}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`pillar-${num}-description`}>Descripción</Label>
                      <Textarea
                        id={`pillar-${num}-description`}
                        value={settings[`pillar_${num}_description`]}
                        onChange={(e) => handleChange(`pillar_${num}_description`, e.target.value)}
                        placeholder={`Descripción del pilar ${num}`}
                        rows={2}
                        className="mt-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Custom Trips Section Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Sección "Viajes a Medida"</h2>
                </div>
                <Button 
                  onClick={() => handleSaveSection('Viajes a Medida', ['custom_trips_title', 'custom_trips_subtitle', 'custom_trips_button'])}
                  disabled={saving['Viajes a Medida']}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving['Viajes a Medida'] ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="custom-trips-title">Título</Label>
                  <Input
                    id="custom-trips-title"
                    value={settings.custom_trips_title}
                    onChange={(e) => handleChange('custom_trips_title', e.target.value)}
                    placeholder="Viajes a Medida"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="custom-trips-subtitle">Subtítulo</Label>
                  <Textarea
                    id="custom-trips-subtitle"
                    value={settings.custom_trips_subtitle}
                    onChange={(e) => handleChange('custom_trips_subtitle', e.target.value)}
                    placeholder="Ya no se viaja para conocer lugares sino para vivirlos..."
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="custom-trips-button">Texto del Botón</Label>
                  <Input
                    id="custom-trips-button"
                    value={settings.custom_trips_button}
                    onChange={(e) => handleChange('custom_trips_button', e.target.value)}
                    placeholder="Diseña tu Viaje"
                    className="mt-2"
                  />
                </div>
              </div>
            </Card>

            {/* Footer Section Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Sección Footer</h2>
                </div>
                <Button 
                  onClick={() => handleSaveSection('Footer', ['footer_description', 'footer_address', 'footer_phone', 'footer_email', 'whatsapp_phone', 'contact_email'])}
                  disabled={saving.Footer}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving.Footer ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="footer-description">Descripción</Label>
                  <Textarea
                    id="footer-description"
                    value={settings.footer_description}
                    onChange={(e) => handleChange('footer_description', e.target.value)}
                    placeholder="Descripción de la empresa..."
                    rows={2}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="footer-address">Dirección</Label>
                  <Input
                    id="footer-address"
                    value={settings.footer_address}
                    onChange={(e) => handleChange('footer_address', e.target.value)}
                    placeholder="Calle Principal 123, Madrid, España"
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="footer-phone">Teléfono</Label>
                    <Input
                      id="footer-phone"
                      value={settings.footer_phone}
                      onChange={(e) => handleChange('footer_phone', e.target.value)}
                      placeholder="+34 900 123 456"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="footer-email">Email</Label>
                    <Input
                      id="footer-email"
                      type="email"
                      value={settings.footer_email}
                      onChange={(e) => handleChange('footer_email', e.target.value)}
                      placeholder="reservas@viajesensueno.com"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="whatsapp-phone">Teléfono WhatsApp (formato internacional)</Label>
                  <Input
                    id="whatsapp-phone"
                    value={settings.whatsapp_phone}
                    onChange={(e) => handleChange('whatsapp_phone', e.target.value)}
                    placeholder="34678744482"
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Número al que se redirigirá a los clientes cuando el chat esté offline.
                  </p>
                </div>

                <div>
                  <Label htmlFor="contact-email">Email(s) de Notificación (Reservas y Mensajes)</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    multiple
                    value={settings.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    placeholder="reservas@viajesensueno.com, ventas@viajesensueno.com"
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Cada nueva reserva o mensaje de contacto enviará un correo con todo el detalle a esta dirección.
                    Para varios destinatarios, sepáralos con comas.
                  </p>
                </div>
              </div>
            </Card>
          </div>
            )}
          </TabsContent>

          <TabsContent value="admins" className="mt-6">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Gestión de Administradores
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Los administradores listados aquí pueden acceder al panel. Cada uno configura su app Authenticator (MFA) con un QR en su primer inicio de sesión.
                  </p>
                </div>
                <Button onClick={() => setShowAddAdmin(!showAddAdmin)} variant={showAddAdmin ? "secondary" : "default"}>
                  {showAddAdmin ? 'Cancelar' : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Añadir Admin
                    </>
                  )}
                </Button>
              </div>

              {showAddAdmin && (
                <div className="mb-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-medium mb-4">Nuevo Administrador</h3>
                  <form onSubmit={handleAddAdmin} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="new-username">Usuario</Label>
                      <Input 
                        id="new-username"
                        value={newAdmin.username}
                        onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                        placeholder="ej. JuanPerez"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-email">Correo Electrónico</Label>
                      <Input 
                        id="new-email"
                        type="email"
                        value={newAdmin.email}
                        onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                        placeholder="juan@viajesensueno.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password">Contraseña</Label>
                      <Input 
                        id="new-password"
                        type="password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                        placeholder="••••••••"
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                      <Button type="submit">Crear Administrador</Button>
                    </div>
                  </form>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Usuario</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Authenticator</th>
                      <th className="text-right py-3 px-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingAdmins ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8 text-muted-foreground">Cargando administradores...</td>
                      </tr>
                    ) : (
                      admins.map((admin) => (
                        <tr key={admin.id} className="border-b hover:bg-slate-50 transition-colors">
                          <td className="py-3 px-4 font-medium">{admin.username}</td>
                          <td className="py-3 px-4 text-muted-foreground">{admin.email}</td>
                          <td className="py-3 px-4">
                            {admin.mfa_enabled ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Configurado
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Pendiente
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {admin.mfa_enabled && admin.id !== currentUserId && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Resetear Authenticator"
                                onClick={() => handleResetMfa(admin)}
                              >
                                <Smartphone className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteAdmin(admin.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
